/**
 * DetailsStep.tsx - Fundraiser Builder Step 1: Basic Details
 *
 * This component handles the first step of the fundraiser builder where users
 * input basic fundraiser information, media, and core settings.
 *
 * Key Features:
 * - Expandable sections for organized form layout
 * - Main fundraiser information (name, title, dates, goal)
 * - Mission statement and description editing
 * - Impact highlights management
 * - Media upload and management
 * - Statistics configuration
 * - Call-to-action customization
 * - Legal information and terms
 * - Marketing and redirect settings
 *
 * Form Sections:
 * - Main Section: Name, title, subtitle, dates, goal, category
 * - Mission Section: Mission title and description
 * - Impact Section: Impact highlights with icons and metrics
 * - Media Section: Image and video upload management
 * - Stats Section: Custom statistics configuration
 * - Call-to-Action Section: CTA customization
 * - Legal Section: Terms, privacy, and legal information
 * - Marketing Section: Redirect settings and promoter goals
 *
 * Props:
 * - currentStep?: number - Current builder step for validation
 * - setCurrentStep?: (step: number) => void - Step navigation handler
 *
 * Dependencies: MobX store, section components, form validation
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import {
  Calendar,
  Plus,
  X,
  Heart,
  ChevronDown,
  ChevronUp,
  Minus,
} from "lucide-react";
import * as Icons from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { IconSelectorModal } from "./IconSelectorModal";
import { MainSection } from "./details/MainSection";
import { MediaSection } from "./details/MediaSection";
import { MissionSection } from "./details/MissionSection";
import { ImpactSection } from "./details/ImpactSection";
import { StatsSection } from "./details/StatsSection";
import { CallToActionSection } from "./details/CallToActionSection";
import { MarketingSection } from "./details/MarketingSection";
import { LegalSection } from "./details/LegalSection";
import FullLibraryView from "../FullLibraryView";
import { MediaItem } from "@mindmemobile/mm-shared-library";
import { MediaModel } from "../../models/GalleryModels";
import { BuilderSectionId, CreateFundraiserModel } from "../../models";

interface DetailsStepProps {
  currentStep: number;
  setCurrentStep: (step: number) => void;
}

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  .main-section-wrapper {
    .checkout-color-picker {
      transform: translate(55px, -1px);
    }
  }
`;

const GlobalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-bottom: 8px;
`;

const GlobalActionButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  color: ${(props) => (props.disabled ? "#9ca3af" : "#152837")};
  font-size: 14px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover:not(:disabled) {
    color: rgba(21, 40, 55, 0.8);
    text-decoration: underline;
  }

  &:active:not(:disabled) {
    color: rgba(21, 40, 55, 0.9);
  }

  svg {
    width: 14px;
    height: 14px;
    opacity: ${(props) => (props.disabled ? 0.4 : 1)};
    transition: opacity 0.2s ease;
  }
`;

const CollapsibleSection = styled.div<{ isExpanded: boolean }>`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  /* overflow: visible; */ // creating issue while opening sections
  /* overflow: hidden; */
  overflow: ${(props) => (props.isExpanded ? "visible" : "hidden")};
  transition: all 0.3s ease;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  ${(props) =>
    props.isExpanded &&
    `
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  `}
`;

const SectionHeader = styled.div<{ isExpanded: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.isExpanded ? "#f8fafc" : "#ffffff")};
  border-bottom: ${(props) =>
    props.isExpanded ? "1px solid #e2e8f0" : "none"};

  &:hover {
    background: #f8fafc;
  }
`;

const SectionHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const SectionDescription = styled.p<{ visible: boolean }>`
  font-size: 16px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  max-height: ${(props) => (props.visible ? "40px" : "0")};
  transition: all 0.3s ease;
  overflow: hidden;
`;

const ExpandButton = styled.button<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: ${(props) => (props.isExpanded ? "#6b7280" : "#f1f5f9")};
  color: ${(props) => (props.isExpanded ? "#ffffff" : "#64748b")};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${(props) => (props.isExpanded ? "#4b5563" : "#e2e8f0")};
    transform: scale(1.05);
  }
`;

const SectionContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${(props) => (props.isExpanded ? "2000px" : "0")};
  opacity: ${(props) => (props.isExpanded ? 1 : 0)};
  overflow: visible;
  /* overflow: hidden; */
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  > div {
    background: #f8fafc;
    border: none;
    border-radius: 0;
    margin: 0;
  }
`;

interface SectionConfig {
  id: BuilderSectionId;
  title: string;
  description: string;
  component: React.ReactNode;
}

export const DetailsStep: React.FC<DetailsStepProps> = observer(
  ({ currentStep, setCurrentStep }) => {
    const { builderData } = fundraiserStore;
    const errors = fundraiserStore.builderData.errors;
    const [touchedFields, setTouchedFields] = React.useState<
      Record<string, boolean>
    >({});
    const [showIconSelector, setShowIconSelector] = useState(false);
    const [iconSelectorImpactId, setIconSelectorImpactId] = useState<
      string | null
    >(null);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
      () => {
        // Try to restore from store or previous session
        return fundraiserStore.lastExpandedSections?.size
          ? new Set(fundraiserStore.lastExpandedSections)
          : new Set(["details"]);
      }
    );
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

    useEffect(() => {
      const hasMediaError = builderData.hasError("mediaItems");
      // const shouldScrollToMedia = fundraiserStore.shouldScrollToMediaSection;

      if (hasMediaError) {
        // Ensure media section is expanded
        const newExpanded = new Set(expandedSections);
        newExpanded.add("media");
        setExpandedSections(newExpanded);
      }
    }, [builderData.errors]);

    useEffect(() => {
      fundraiserStore.lastExpandedSections = new Set(expandedSections);
    }, [expandedSections]);

    const toggleSection = useCallback((section: string) => {
      setExpandedSections((prev) => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(section)) {
          newExpanded.delete(section);
        } else {
          newExpanded.add(section);
        }
        return newExpanded;
      });
    }, []);

    const updateBuilderField = (
      field: keyof CreateFundraiserModel,
      value: any
    ) => {
      fundraiserStore.updateBuilderField(field, value);
    };

    const handleFieldBlur = (fieldName: string) => {
      const isValidated = builderData.validate(false);
      setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    };

    const shouldShowError = (fieldName: string) => {
      return touchedFields[fieldName] && errors[fieldName] ? true : false;
    };

    // Helper function to determine if a color is light or dark
    const isLightColor = (hexColor: string): boolean => {
      const hex = hexColor?.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 155;
    };

    // Helper function to darken a color
    const darkenColor = (hexColor: string, percent: number = 40): string => {
      const hex = hexColor?.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      const darkenedR = Math.max(0, Math.floor(r * (1 - percent / 100)));
      const darkenedG = Math.max(0, Math.floor(g * (1 - percent / 100)));
      const darkenedB = Math.max(0, Math.floor(b * (1 - percent / 100)));

      return `#${darkenedR.toString(16).padStart(2, "0")}${darkenedG.toString(16).padStart(2, "0")}${darkenedB.toString(16).padStart(2, "0")}`;
    };

    // Helper function to get icon colors based on background
    const getIconColors = (selectedColor: string) => {
      const bgColor = selectedColor;
      const iconColor = isLightColor(selectedColor)
        ? darkenColor(selectedColor)
        : "#ffffff";
      return { bgColor, iconColor };
    };
    const renderIcon = (iconName: string) => {
      const Icon = (Icons as any)[iconName];
      return Icon ? <Icon size={16} /> : <Heart size={16} />;
    };

    const handleImageUpload = (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const file = files[0];
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          fundraiserStore.updateBuilderField("fundraiserImage", result);
        };
        reader.readAsDataURL(file);
      }
    };

    const handleCtaImageUpload = (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const file = files[0];
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          updateBuilderField("ctaImage", result);
        };
        reader.readAsDataURL(file);
      }
    };

    const handleExpandAll = () => {
      const allSectionIds: BuilderSectionId[] = [
        "details",
        "media",
        "mission",
        "impact",
        "stats",
        "callToAction",
        "marketing",
        "legal",
        "donation",
        "design",
        "promoters",
      ];
      setExpandedSections(new Set(allSectionIds));
    };

    const handleCollapseAll = () => {
      // Collapse all sections including Details
      setExpandedSections(new Set());
    };

    // Check if all sections are expanded
    const allSectionIds: BuilderSectionId[] = [
      "details",
      "media",
      "mission",
      "impact",
      "stats",
      "callToAction",
      "marketing",
      "legal",
    ];
    const allExpanded = allSectionIds.every((sectionId) =>
      expandedSections.has(sectionId)
    );

    // Check if all sections are collapsed
    const allCollapsed = expandedSections.size === 0;

    const handleSectionKeyDown = (
      e: React.KeyboardEvent,
      sectionId: string,
      isLastField: boolean
    ) => {
      if (e.key === "Tab" && !e.shiftKey && isLastField) {
        e.preventDefault();

        // Find next section
        const sectionOrder = [
          "details",
          "media",
          "mission",
          "impact",
          "stats",
          "callToAction",
          "marketing",
          "legal",
        ];
        const currentIndex = sectionOrder.indexOf(sectionId);
        const nextIndex = currentIndex + 1;

        if (nextIndex < sectionOrder.length) {
          const nextSectionId = sectionOrder[nextIndex];

          // Close current section and open next
          const newExpanded = new Set([nextSectionId]);
          setExpandedSections(newExpanded);

          // Focus the next section after a brief delay
          setTimeout(() => {
            const nextSectionRef = sectionRefs.current[nextSectionId];
            if (nextSectionRef) {
              const firstInput = nextSectionRef.querySelector(
                'input, textarea, select, button[type="button"]'
              ) as HTMLElement;
              if (firstInput) {
                firstInput.focus();
              }
            }
          }, 100);
        }
      }
    };

    const sections: SectionConfig[] = [
      {
        id: "details",
        title: "Main",
        description:
          "Basic fundraiser information including name, dates, and branding",
        component: (
          <MainSection
            builderData={builderData}
            categories={fundraiserStore.categories}
            updateBuilderField={updateBuilderField}
            shouldShowError={shouldShowError}
            handleFieldBlur={handleFieldBlur}
            renderIcon={renderIcon}
            getIconColors={getIconColors}
            setShowIconSelector={setShowIconSelector}
            setIconSelectorImpactId={setIconSelectorImpactId}
            handleImageUpload={handleImageUpload}
            onKeyDown={(e, isLastField) =>
              handleSectionKeyDown(e, "details", isLastField === true)
            }
          />
        ),
      },
      {
        id: "media",
        title: "Media",
        description: "Images and videos to showcase your fundraiser",
        component: (
          <MediaSection
            onKeyDown={(e, isLastField) =>
              handleSectionKeyDown(e, "media", isLastField)
            }
          />
        ),
      },
      {
        id: "mission",
        title: "Mission",
        description: "Your mission statement and cause description",
        component: (
          <MissionSection
            builderData={builderData}
            updateBuilderField={updateBuilderField}
            handleFieldBlur={handleFieldBlur}
            onKeyDown={(e, isLastField) =>
              handleSectionKeyDown(e, "mission", isLastField)
            }
          />
        ),
      },
      {
        id: "impact",
        title: "Impact",
        description: "Key metrics and impact highlights for your cause",
        component: (
          <ImpactSection
            builderData={builderData}
            onKeyDown={(e, isLastField) =>
              handleSectionKeyDown(e, "impact", isLastField)
            }
          />
        ),
      },
      {
        id: "stats",
        title: "Stats",
        description: "Custom statistics to display on your fundraiser page",
        component: (
          <StatsSection
            builderData={builderData}
            shouldShowError={shouldShowError}
            onKeyDown={(e, isLastField) =>
              handleSectionKeyDown(e, "stats", isLastField)
            }
          />
        ),
      },
      {
        id: "callToAction",
        title: "Call to Action",
        description: "Compelling call-to-action section with custom messaging",
        component: (
          <CallToActionSection
            builderData={builderData}
            updateBuilderField={updateBuilderField}
            renderIcon={renderIcon}
            getIconColors={getIconColors}
            setShowIconSelector={setShowIconSelector}
            setIconSelectorImpactId={setIconSelectorImpactId}
            handleCtaImageUpload={handleCtaImageUpload}
            onKeyDown={(e, isLastField) =>
              handleSectionKeyDown(e, "callToAction", isLastField === true)
            }
          />
        ),
      },
      {
        id: "marketing",
        title: "Marketing",
        description:
          "Set goals, reward promoters for getting donations, thank sponsors",
        component: (
          <MarketingSection
            builderData={builderData}
            updateBuilderField={updateBuilderField}
            handleFieldBlur={handleFieldBlur}
            onKeyDown={(e, isLastField) =>
              handleSectionKeyDown(e, "marketing", isLastField)
            }
          />
        ),
      },
      {
        id: "legal",
        title: "Legal",
        description: "Legal configuration and compliance settings",
        component: (
          <LegalSection
            builderData={builderData}
            updateBuilderField={updateBuilderField}
            handleFieldBlur={handleFieldBlur}
            onKeyDown={(e, isLastField) =>
              handleSectionKeyDown(e, "legal", isLastField)
            }
          />
        ),
      },
    ];
    // Watch for validation errors that require navigation to Details step
    React.useEffect(() => {
      const hasMediaError = fundraiserStore.builderData.hasError("mediaItems");

      // If there's a media error and we're not already on Details step, navigate there
      if (hasMediaError && currentStep !== 1) {
        setCurrentStep(1);
        fundraiserStore.currentBuilderStep = 1;
      }
    }, [fundraiserStore.builderData.errors, currentStep]);

    return (
      <StepContainer>
        <GlobalActions>
          <GlobalActionButton
            onClick={handleExpandAll}
            disabled={allExpanded}
            title="Expand all sections"
          >
            <Plus />
            Expand All
          </GlobalActionButton>
          <GlobalActionButton
            onClick={handleCollapseAll}
            disabled={allCollapsed}
            title="Collapse all sections"
          >
            <Minus />
            Collapse All
          </GlobalActionButton>
        </GlobalActions>

        {sections.map((section) => (
          <CollapsibleSection
            key={section.id}
            isExpanded={expandedSections.has(section.id)}
            ref={(el) => (sectionRefs.current[section.id] = el)}
            style={{ marginTop: section.id === "details" ? "0px" : "20px" }}
          >
            <SectionHeader
              isExpanded={expandedSections.has(section.id)}
              onClick={() => toggleSection(section.id)}
            >
              <SectionHeaderContent>
                <SectionTitle>{section.title}</SectionTitle>
                <SectionDescription visible={!expandedSections.has(section.id)}>
                  {section.description}
                </SectionDescription>
              </SectionHeaderContent>
              <ExpandButton isExpanded={expandedSections.has(section.id)}>
                {expandedSections.has(section.id) ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </ExpandButton>
            </SectionHeader>
            <SectionContent isExpanded={expandedSections.has(section.id)}>
              {section.component}
            </SectionContent>
          </CollapsibleSection>
        ))}

        <IconSelectorModal
          visible={showIconSelector}
          selectedIcon={
            iconSelectorImpactId === "cta"
              ? builderData.ctaIcon || "Heart"
              : builderData.titleIcon || "Heart"
          }
          onClose={() => setShowIconSelector(false)}
          onSelect={(iconName) => {
            if (iconSelectorImpactId === "cta") {
              updateBuilderField("ctaIcon", iconName);
            } else {
              updateBuilderField("titleIcon", iconName);
            }
            setIconSelectorImpactId(null);
          }}
        />
      </StepContainer>
    );
  }
);
